import {Component, forwardRef, Injector, Input, OnChanges, OnInit, SimpleChanges} from '@angular/core';
import {
  ControlContainer,
  ControlValueAccessor,
  FormControl,
  FormGroup,
  NG_VALUE_ACCESSOR, ValidationErrors, NG_VALIDATORS, Validators, AbstractControl, NgControl
} from '@angular/forms';
import dayjs from 'dayjs'
import {AbstractComponent} from "../abstract.component";
import {required} from "@utility";

@Component({
    selector: 'app-date-time-from-to-input',
    templateUrl: './date-time-from-to-input.component.html',
    styleUrls: ['./date-time-from-to-input.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: forwardRef(() => DateTimeFromToInputComponent)
        },
        {
            provide: NG_VALIDATORS,
            multi: true,
            useExisting: DateTimeFromToInputComponent
        }
    ],
    standalone: false
})
export class DateTimeFromToInputComponent extends AbstractComponent implements OnInit, OnChanges, ControlValueAccessor, Validators {
  @Input() labelStart: string = "";
  @Input() labelEnd: string = "";
  @Input() displayTime: boolean = true;
  @Input() invalidMessageKey: string = ""
  @Input() ranges: { from: Date | undefined, to: Date | undefined } | undefined;
  @Input() formControlName?: string;
  @Input() calendarStart: Date | undefined;
  @Input() displayRangeErrorBeforeTouched: boolean = false;

  form: FormGroup = new FormGroup<any>({
    from: new FormControl(),
    to: new FormControl(),
  });

  fromCalendarAvailableDates: { from: Date | undefined, to: Date | undefined } = {from: undefined, to: undefined};
  toCalendarAvailableDates: { from: Date | undefined, to: Date | undefined } = {from: undefined, to: undefined};
  invalidRanges: boolean = false;

  constructor(private controlContainer: ControlContainer, private inj: Injector) {
    super();
  }

  onChange: Function = () => {
  };
  onTouched: Function = () => {
  };

  setDisabledState(disabled: boolean) {
    if (disabled) {
      this.form.disable();
    } else {
      this.form.enable();
    }
  }

  ngOnInit() {
    if (this.control.hasValidator(required)) {
      this.form.controls["from"].addValidators([required]);
      this.form.controls["from"].updateValueAndValidity();
      this.form.controls["to"].addValidators([required]);
      this.form.controls["to"].updateValueAndValidity();
    }
    this.initTimes();
    this.subscriptions.push(this.form.controls["from"].valueChanges.subscribe(_ => {
      this.checkDates("from", "to");
      this.initTimes();
      this.checkRanges();
      this.onChange({from: this.form.controls["from"].value, to: this.form.controls["to"].value})
    }))
    this.subscriptions.push(this.form.controls["to"].valueChanges.subscribe(_ => {
      this.checkDates("to", "from");
      this.initTimes();
      this.checkRanges();
      this.onChange({from: this.form.controls["from"].value, to: this.form.controls["to"].value});
    }))
  }

  ngOnChanges(changes: SimpleChanges) {
    if (JSON.stringify(changes['ranges']?.currentValue) !== JSON.stringify(changes['ranges']?.previousValue)) {
      this.form.controls["from"].updateValueAndValidity();
      this.form.controls["to"].updateValueAndValidity();
    }
  }

  ngAfterViewInit() {
    const outerControl: AbstractControl | null = this.inj.get(NgControl).control;
    if (outerControl) {
      outerControl.markAsTouched = () => {
        this.markAllAsTouched(this.form);
      };
      outerControl.markAsUntouched = () => {
        this.markAllAsUntouched(this.form)
      };
    }
  }

  get control(): FormControl {
    return <FormControl<any>>this.controlContainer!.control!.get(this.formControlName!);
  }

  registerOnChange(onChange: any) {
    this.onChange = onChange;
  }

  registerOnTouched(onTouched: Function): void {
    this.onTouched = onTouched;
  }

  writeValue(value: { from: Date | string | undefined, to: Date | string | undefined } | undefined) {
    if (value?.from) {
      const from = new Date(value.from);
      this.form.controls["from"].setValue(from)
    }
    if (value?.to) {
      const to = new Date(value.to);
      this.form.controls["to"].setValue(to)
    }
    this.checkRanges();
  }

  private getFromCalendarAvailableRange(): { from: Date | undefined; to: Date | undefined } {
    const from = this.ranges?.from ? new Date(this.ranges.from) : undefined;
    let to = this.ranges?.to ? new Date(this.ranges.to) : undefined;
    const current = new Date();

    const end = this.form.controls["to"].value;
    if (end) {
      to = to && this.isSameOrBefore(end, to) ? end : to || end;
      to = to && this.isSameOrAfter(to, current) ? to : undefined;
    }

    return { from, to };
  }

  private getToCalendarAvailableRange(): { from: Date | undefined, to: Date | undefined } {
    let from: Date | undefined = this.ranges?.from ? new Date(this.ranges.from) : undefined;
    let to: Date | undefined = this.ranges?.to ? new Date(this.ranges.to) : undefined;
    if (this.form.controls["from"].value) {
      const start = this.form.controls["from"].value
      from = from ? (this.isSameOrAfter(start, from) ? start : from) : start
    }
    return {from: from, to: to};
  }

  private checkDates(controlChanged: string, other: string) {
    let from = this.form.controls["from"].value;
    let to = this.form.controls["to"].value;
    this.removeError(this.form.controls[other], "date");
    this.removeError(this.form.controls[controlChanged], "date");
    if (from && to) {
      if (dayjs(from).isAfter(to, "minutes")) {
        this.form.controls[controlChanged].setErrors({"date": true})

      }
    }
  }

  private checkRanges() {
    let from = this.form.controls["from"].value;
    let to = this.form.controls["to"].value;
    this.invalidRanges = false;
    if (from && to) {
      if (this.isInAllowedRange(from) && this.isInAllowedRange(to)) {
        this.removeError(this.form, "range")
        this.invalidRanges = false;
      } else {
        this.form.setErrors({"range": true}, {emitEvent: true})
        this.invalidRanges = true;
      }
    } else {
      this.removeError(this.form, "range")
      this.invalidRanges = false;
    }
  }

  private initTimes(): void {
    this.toCalendarAvailableDates = this.getToCalendarAvailableRange();
    this.fromCalendarAvailableDates = this.getFromCalendarAvailableRange();
  }

  private isSameOrAfter(date: Date, compareTo: Date): boolean {
    return dayjs(date).isAfter(dayjs(compareTo), this.displayTime ? 'minutes' : 'days')
      || dayjs(date).isSame(dayjs(compareTo), this.displayTime ? 'minutes' : 'days')
  }

  private isSameOrBefore(date: Date, compareTo: Date): boolean {
    return dayjs(date).isBefore(dayjs(compareTo), this.displayTime ? 'minutes' : 'days')
      || dayjs(date).isSame(dayjs(compareTo), this.displayTime ? 'minutes' : 'days')
  }

  private isInAllowedRange(date: Date) {
    if (this.ranges?.from && this.ranges?.to) {
      return this.isSameOrAfter(date, this.ranges.from) && this.isSameOrBefore(date, this.ranges.to);
    } else if (this.ranges?.from && !this.ranges.to) {
      return this.isSameOrAfter(date, this.ranges.from);
    } else if (this.ranges?.to && !this.ranges.from) {
      return this.isSameOrBefore(date, this.ranges.to);
    }
    return true;
  }

  validate(_: AbstractControl): ValidationErrors | null {
    this.checkRanges();
    const fromErrors = this.form.controls["from"].errors;
    const toErrors = this.form.controls["to"].errors;
    let errors: ValidationErrors = {...fromErrors, ...toErrors, ...this.form.errors};
    return Object.keys(errors).length ? errors : null;
    return null;
  }

}



